/**
 * Static Tooth Chart preview cards for the "All" visual overview.
 *
 * Renders one card per saved odontogram (newest first), with a mini-rendering
 * of the 32 adult teeth in two arches. Matches the sizing of the surrounding
 * grid and uses condition / crown / missing colors to summarize chart state.
 *
 * Click opens the focused Tooth Chart workspace.
 */

import { usePowerSyncQuery } from '../../../adapters/powersync';
import {
  qOdontogramChartsByEncounter,
  type OdontogramChartRow,
} from '@helios/data-access';
import type { ToothStatesMap } from './OdontogramChart';

const ADULT_UPPER = [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28];
const ADULT_LOWER = [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38];

const CONDITION_COLORS: Record<string, string> = {
  caries: '#dc2626',
  composite: '#3b82f6',
  amalgam: '#6b7280',
  recurrent_caries: '#f97316',
};

const CROWN_COLORS: Record<string, string> = {
  ceramic: '#e2e8f0',
  pfm: '#fbbf24',
  gold: '#f59e0b',
  metal: '#9ca3af',
};

function parseToothStates(raw: string | null): ToothStatesMap {
  if (!raw) return {};
  try {
    return JSON.parse(raw) as ToothStatesMap;
  } catch {
    return {};
  }
}

function toothFill(state: ToothStatesMap[string] | undefined): {
  fill: string;
  isMissing: boolean;
} {
  if (!state) return { fill: '#e5e7eb', isMissing: false };
  if (state.flags?.includes('missing')) return { fill: '#fecaca', isMissing: true };
  if (state.conditions && state.conditions.length > 0) {
    const code = state.conditions[0].code;
    return { fill: CONDITION_COLORS[code] ?? '#94a3b8', isMissing: false };
  }
  if (state.crown) {
    return { fill: CROWN_COLORS[state.crown] ?? '#94a3b8', isMissing: false };
  }
  return { fill: '#e5e7eb', isMissing: false };
}

interface Props {
  patientId: string;
  encounterId: string;
  onOpen?: (chartId: string) => void;
}

export function SavedToothChartPreview({ encounterId, onOpen }: Props) {
  const { data: chartRows = [] } = usePowerSyncQuery<OdontogramChartRow>(
    qOdontogramChartsByEncounter(encounterId),
  );

  if (chartRows.length === 0) return null;

  return (
    <>
      {chartRows.map((chart) => (
        <ToothChartCard
          key={chart.id}
          chart={chart}
          onClick={onOpen ? () => onOpen(chart.id) : undefined}
        />
      ))}
    </>
  );
}

function ToothChartCard({
  chart,
  onClick,
}: {
  chart: OdontogramChartRow;
  onClick?: () => void;
}) {
  const states = parseToothStates(chart.tooth_states);
  const SIZE = 200;
  const cols = 16;
  const cellW = SIZE / cols;
  const dotR = cellW / 2 - 1.5;
  const upperY = SIZE * 0.35;
  const lowerY = SIZE * 0.65;

  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      title="Open Tooth Chart"
      style={{
        position: 'relative',
        aspectRatio: '1',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        border: '1px solid var(--border)',
        background: '#fff',
        cursor: onClick ? 'pointer' : 'default',
        padding: 0,
        width: '100%',
      }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block' }}
      >
        <line
          x1={4}
          y1={SIZE / 2}
          x2={SIZE - 4}
          y2={SIZE / 2}
          stroke="#e5e7eb"
          strokeWidth={1}
        />
        {ADULT_UPPER.map((num, i) => {
          const { fill, isMissing } = toothFill(states[String(num)]);
          const cx = i * cellW + cellW / 2;
          return (
            <g key={`u-${num}`}>
              <circle cx={cx} cy={upperY} r={dotR} fill={fill} stroke="#cbd5e1" strokeWidth={0.6} />
              {isMissing && (
                <line
                  x1={cx - dotR}
                  y1={upperY - dotR}
                  x2={cx + dotR}
                  y2={upperY + dotR}
                  stroke="#dc2626"
                  strokeWidth={1.2}
                />
              )}
            </g>
          );
        })}
        {ADULT_LOWER.map((num, i) => {
          const { fill, isMissing } = toothFill(states[String(num)]);
          const cx = i * cellW + cellW / 2;
          return (
            <g key={`l-${num}`}>
              <circle cx={cx} cy={lowerY} r={dotR} fill={fill} stroke="#cbd5e1" strokeWidth={0.6} />
              {isMissing && (
                <line
                  x1={cx - dotR}
                  y1={lowerY - dotR}
                  x2={cx + dotR}
                  y2={lowerY + dotR}
                  stroke="#dc2626"
                  strokeWidth={1.2}
                />
              )}
            </g>
          );
        })}
      </svg>
      <span
        style={{
          position: 'absolute',
          bottom: 4,
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '1px 8px',
          borderRadius: 'var(--radius-sm)',
          background: 'rgba(0,0,0,0.6)',
          color: '#fff',
          fontSize: 9,
          fontWeight: 600,
          letterSpacing: '0.3px',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}
      >
        Tooth Chart
      </span>
    </button>
  );
}
